// Slice 2L · Levi Schedule Awareness card.
//
// Mirrors the "LeviReviewSection" idiom already used by Slice 2N
// Profitability (`frontend/src/pages/Profitability.jsx` :: line 690):
//   * Fetch a tenant-scoped, backend-computed `levi-context` payload.
//   * Render deterministic bullet observations from that payload.
//   * Never call an LLM. Never mutate schedule state. Never contact a
//     customer.
//
// "Levi recommends. The contractor decides."
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { HeartHandshake, ShieldCheck, AlertTriangle, Info, ArrowRight } from "lucide-react";
import { deriveScheduleObservations } from "@/lib/leviSchedule";

const toneIcon = (tone) => {
  if (tone === "warning") return AlertTriangle;
  if (tone === "success") return ShieldCheck;
  return Info;
};

const toneClasses = (tone) => {
  if (tone === "warning") return "text-amber-800 border-amber-500/30 bg-amber-500/10";
  if (tone === "success") return "text-emerald-800 border-emerald-500/30 bg-emerald-500/10";
  return "text-slate-800 border-slate-300 bg-slate-50";
};

const LeviScheduleAwareness = () => {
  const { t, lang } = useLang();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/schedule-events/levi-context")
      .then((r) => { if (!cancelled) setCtx(r.data || null); })
      .catch(() => { if (!cancelled) { setCtx(null); setErrored(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // If the endpoint failed hard, stay quiet — never crash the calendar
  // page over a Levi advisory strip.
  if (errored && !ctx) return null;
  if (loading) return null;

  const observations = deriveScheduleObservations(ctx || {}, t, { lang });
  if (observations.length === 0) return null;

  return (
    <section
      data-testid="levi-schedule-awareness"
      className="jc-card p-5 mb-5"
    >
      <header className="flex items-start gap-2 mb-3">
        <HeartHandshake size={16} className="text-[var(--jc-brand,#4f46e5)] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="overline text-[10px] uppercase tracking-widest text-[var(--jc-text-soft,#64748b)]">
            {t("schedule.company.levi.section_overline")}
          </div>
          <h2 className="text-base font-semibold" data-testid="levi-schedule-title">
            {t("schedule.company.levi.section_title")}
          </h2>
          <p className="text-xs text-[var(--jc-text-soft,#64748b)] mt-0.5">
            {t("schedule.company.levi.section_subtitle")}
          </p>
        </div>
      </header>
      <ul className="space-y-2" data-testid="levi-schedule-observations">
        {observations.map((o) => {
          const Icon = toneIcon(o.tone);
          return (
            <li
              key={o.key}
              data-testid={`levi-schedule-obs-${o.key}`}
              className={`flex items-start gap-2 text-sm rounded border px-3 py-2 ${toneClasses(o.tone)}`}
            >
              <Icon size={14} className="shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div>{o.text}</div>
                {o.action && o.action.to ? (
                  <Link
                    to={o.action.to}
                    data-testid={`levi-schedule-action-${o.key}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--jc-blue,#4f46e5)] hover:underline mt-1"
                  >
                    {o.action.label} <ArrowRight size={12} />
                  </Link>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default LeviScheduleAwareness;
